<template>
  <div class="form-upload-container">
    
    <div v-for="file in fileList" :key="file.uppyId" :class="['upload-file-item', file.uploading ? 'uploading' : '']">
      <a @click.prevent="removeFile(file)" class="upload-close-button">
        <Icon icon="fa-times" />
      </a>
      <div class="upload-content">
        <div class="upload-file-name-container">
          <span class="upload-file-name">
            <Icon icon="fa-upload" class="mr-2" v-if="file.uploading" />
            <Icon icon="fa-download" class="mr-2" v-if="file.downloading" />
            {{file.name}}
          </span>
        </div>
        <div class="progress upload-progress-bar" v-if="file.uploading">
          <div class="progress-bar" role="progressbar" :style="{'width': `${file.progress}%`}" :aria-valuenow="file.progress" aria-valuemin="0" aria-valuemax="100"></div>
        </div>
        <div class="progress upload-progress-bar" v-if="file.downloading">
          <div class="progress-bar bg-warning" role="progressbar" :style="{'width': `${file.progress}%`}" :aria-valuenow="file.progress" aria-valuemin="0" aria-valuemax="100"></div>
        </div>
        <Icon icon="fa-check-circle" v-if="file.uploaded" class="upload-file-result status-active" />
        <Icon icon="fa-exclamation-circle" v-if="file.error" class="upload-file-result status-warning" />
      </div>
    </div>

    <div v-for="error in errorFileList" :key="error.uppyId">
      <Icon icon="fa-exclamation-circle" class="status-warning" /> <span class="text-danger">{{error.errorMessage}}</span>
    </div>

    <input
      type="file"
      hidden
      ref="fileInput"
      :multiple="!singleFile"
      :accept="acceptedFileTypes"
      @change="onFileInputChange"
    />
    <div class="upload-file-browse" @click.prevent="onBrowse" v-show="canBrowse">+</div>

    <div class="form-invalid-feedback" v-if="hasError()">
      {{getError()}}
    </div>
    <span v-if="!hasError() && note" class="form-field-note">{{note}}</span>
  </div>
</template>

<style lang="scss">
  @import '~@uppy/core/dist/style.css';

.form-upload-container {
  height: $file-upload-height;
}
.upload-progress-bar {
  height: 3px;
  position: absolute;
  bottom: 2px;
  left: 22px;
  right: 10px;
}
.upload-close-button {
  position: absolute;
  left: 0;
  top: 0;
  text-align: center;
  padding: 0;
  margin: 0;
  font-size: 0.65rem;
  height: $file-upload-height;
  width: 20px;
  line-height: 30px;
  vertical-align: middle;
}
.upload-close-button:hover {
  color: #000000 !important;
  background-color: #f7f7f7;
}
.upload-content {
  display: inline-block;
  margin-left: 20px;
  margin-right: 10px;
  max-width: 160px;
  overflow: hidden;
  height: $file-upload-height;
  line-height: $file-upload-height;
  vertical-align: middle;
}
.upload-file-browse {
  cursor: pointer;
  position: relative;
  display: inline-block;
  vertical-align: top;
  color: #000000;
  font-size: 0.95rem;
  color: #777777;
  background-color: #e0e0e0;
  border-radius: 3px;
  padding: 0.25rem 0.5rem;
  height: $file-upload-height;
}
.upload-file-item {
  position: relative;
  display: inline-block;
  vertical-align: top;
  border: 1px solid #e7e7e7;
  background-color: #ffffff;
  color: #676a6c;
  height: $file-upload-height;
  font-size: 0.7rem;
  border-radius: 3px;
  margin-right: 0.2rem;
  padding-right: 0.65rem;
  min-width: 120px;
  max-width: 180px;
}
.upload-file-item.uploading {
  background-color: #e0e0e0;
  color: #414243;
  border: none;
}
.upload-file-size {
  font-size: 0.6rem;
}
.upload-file-name-container {
  max-width: 140px;
  overflow: hidden;
}
.upload-file-name {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.upload-file-result {
  position: absolute;
  right: 3px;
  top: 0px;
}

</style>

<script>
import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import axios from 'axios'

import AuthService from '@/services/authService'
import formField from '@/mixins/formField'

import Uppy from '@uppy/core'
import AwsS3Multipart from '@uppy/aws-s3-multipart'

export default {
  name: "FormUpload",
  model: {
    event: 'change'
  },
  mixins: [formField],
  props: {
    value: [Number, Array],
    field: String,
    singleFile: {
      type: Boolean,
      default: false
    },
    showBrowseButton: {
      type: Boolean,
      default: true
    },
    autoUpload: {
      type: Boolean,
      default: true
    },
    note: String,
    fileTypes: Array
  },
  computed: {
    acceptedFileTypes() {
      if(this.fileTypes)
        return this.fileTypes.join(',')
      return null
    },
    canBrowse() {
      if(!this.showBrowseButton)
        return false
      if(this.singleFile && this.fileList.length >= 1)
        return false
      return true
    },
    errorFileList() {
      return this.fileList.filter(file => file.error)
    }
  },
  data() {
    return {
      fileList: [],
      uppyId: 'form-upload-uppy-' + this.uuid
    }
  },
  mounted() {
    this.$options.uppy = null
  },
  beforeDestroy() {
    if(this.$options.uppy) {
      this.$options.uppy.close()
      this.$options.uppy = null
    }
  },
  methods: {
    initUppy() {
      if(this.$options.uppy)
        return

      this.$options.uppy = new Uppy({
        id: this.uppyId,
        autoProceed: true,
        debug: true,
        allowMultipeUploads: !this.singleFile,
        onBeforeFileAdded: (currentFile, files) => {
          // Straight up ban all file extensions that have the ability to contain malware / viruses
          let bannedFileExtensions = ['.doc', '.xls', '.exe', '.zip', '.rar', '.scr', '.vbs', '.com']
          let filename = currentFile.name.trim().toLowerCase()
          let bannedFileExt = bannedFileExtensions.find(ext => filename.endsWith(ext))
          if(bannedFileExt != null) {
            this.warningMessage(`Upload Error`, `Files with extension '${bannedFileExt}' are unavailable for uploading`)
            return false
          }          
          
          return true
        },
      })

      // Setup S3 Multipart System
      this.$options.uppy.use(AwsS3Multipart, {
        limit: 2,

        createMultipartUpload: (file) => {
          return new Promise((resolve, reject) => {
            var data = {
              name: file.name,
              type: file.type
            }
            return this.$apiService.coreService.createS3MultipartUpload(data)
            .then(response => {
              return resolve({
                uploadId: response.data.s3UploadId,
                key: response.data.key
              })
            })
            .catch(err => {
              return reject(err)
            })
          })
        },
        listParts: (file, multiPartInfo) => {
          return new Promise((resolve, reject) => {
            return this.$apiService.coreService.listS3MultipartUploadParts(multiPartInfo.uploadId, { key: multiPartInfo.key })
            .then(response => {
              return resolve(response.data.map(part => {
                return {
                  PartNumber: part.partNumber,
                  Size: part.size,
                  ETag: part.eTag
                }
              }))
            })
            .catch(err => {
              return reject(err)
            })
          })
        },
        prepareUploadPart: (file, partData) => {
          return new Promise((resolve, reject) => {
            return this.$apiService.coreService.prepareS3MultipartUploadPart(partData.uploadId, { key: partData.key, partNumber: partData.number })
            .then(response => {
              return resolve({ url: response.data.url })
            })
            .catch(err => {
              return reject(err)
            })
          })
        },
        abortMultipartUpload: (file, multiPartInfo) => {
          return new Promise((resolve, reject) => {
            return this.$apiService.coreService.abortS3MultipartUploadPart(multiPartInfo.uploadId, { key: multiPartInfo.key })
            .then(response => {
              return resolve(response.data)
            })
            .catch(err => {
              return reject(err)
            })
          })
        },
        completeMultipartUpload: (file, multiPartInfo) => {
          return new Promise((resolve, reject) => {
            let partList = multiPartInfo.parts.map(part => {
              return { eTag: part.ETag, partNumber: part.PartNumber }
            })
            return this.$apiService.coreService.completeS3MultipartUpload(multiPartInfo.uploadId, { key: multiPartInfo.key, partList: partList })
            .then(response => {
              // Add in extra details for our processing
              file.key = multiPartInfo.key
              return resolve({ location: response.data.url })
            })
            .catch(err => {
              return reject(err)
            })
          })
        }
      })

      this.$options.uppy.on('file-added', (file) => {
        this.fileList.push({
          uppyId: file.id,
          key: _.clone(file.key),
          uploadId: file.uploadId,
          name: file.name,
          mimeType: file.type,
          url: '',
          uploaded: false,
          uploading: true,
          downloading: false,
          error: false,
          errorMessage: '',
          progress: 0,
          bytesUploaded: 0,
          bytesTotal: 0
        })
        this.emitChangeEvent()
      })

      this.$options.uppy.on('file-removed', (file) => {
        this.fileList = this.fileList.filter(listFile => listFile.uppyId != file.id)
        this.emitChangeEvent()

        // Notify server
        // Don't need to worry if success/failed
        // S3 has a lifecycle rule to clean up expired/incomplete uploads
        if(file.s3Multipart && file.s3Multipart.key) {
          this.$apiService.coreService.deleteS3MultipartUpload({ key: file.s3Multipart.key })
          .then(() => {})
          .catch(err => {})
        }
      })
      this.$options.uppy.on('upload-progress', (file, progress) => {
        var fileInfo = this.fileList.find(listFile => listFile.uppyId == file.id)
        if(fileInfo && progress.bytesTotal) {
          fileInfo.bytesUploaded = progress.bytesUploaded
          fileInfo.bytesTotal = progress.bytesTotal
          fileInfo.progress = Math.round((progress.bytesUploaded / progress.bytesTotal) * 100.0)
        }

        this.emitChangeEvent()
      })

      this.$options.uppy.on('upload-success', (file, response) => {
        var fileInfo = this.fileList.find(listFile => listFile.uppyId == file.id)
        let key = file.key
        if(!key && file.s3Multipart)
          key = file.s3Multipart.key

        if(fileInfo) {
          fileInfo.key = key
          fileInfo.bytesUploaded = fileInfo.bytesTotal
          fileInfo.url = response.uploadURL
          fileInfo.uploading = false
          fileInfo.uploaded = true
        }
        
        this.emitChangeEvent()
      })

      this.$options.uppy.on('upload-error', (file, error, response) => {
        var fileInfo = this.fileList.find(listFile => listFile.uppyId == file.id)
        if(fileInfo) {
          var errorMessage = 'Error while uploading'
          if(error.response && error.response.data) {
            if(error.response.data.error)
              errorMessage = error.response.data.error.message || errorMessage
          }

          fileInfo.uploading = false
          fileInfo.error = true
          fileInfo.errorMessage = errorMessage
          this.emitChangeEvent()
        }
      })
    },
    emitChangeEvent() {
      // Only notify with successfully uploaded files
      this.$emit('change', this.fileList.filter(file => file.uploaded))
      this.$emit('uploading', this.fileList.filter(file => file.uploading).length > 0)
      this.$emit('progress', this.fileList.filter(file => file.uploading || file.downloading).length > 0)
    },
    hasError() {
      return this.getFieldError(this.$props.field).length > 0
    },
    getError() {
      return this.getFieldError(this.$props.field)
    },

    clear() {
      if(this.$options.uppy)
        this.$options.uppy.reset()

      this.fileList = []
      this.emitChangeEvent()
    },

    addRemoteFile(info) {
      this.initUppy()

      let fileListInfo = {
        uppyId: uuidv4(),
        key: null,
        uploadId: null,
        name: info.name,
        mimeType: info.type,
        url: info.url,
        uploaded: false,
        uploading: false,
        downloading: true,
        progress: 0,
        bytesUploaded: 0,
        bytesTotal: 0,
        abortCtrl: new AbortController()
      }

      this.fileList.push(fileListInfo)

      this.emitChangeEvent()

      // Start download
      axios.get(info.url, {
        responseType: 'blob',
        signal: fileListInfo.abortCtrl,
        onDownloadProgress: function (progressEvent) {
          let totalLength = progressEvent.lengthComputable ? progressEvent.total : progressEvent.target.getResponseHeader('content-length') || progressEvent.target.getResponseHeader('x-decompressed-content-length');          
          if(totalLength !== null) {
            fileListInfo.progress = (Math.round( (progressEvent.loaded * 100) / totalLength ))
          }
        },
      })
      .then(response => {
        // Remove download from the list & add the downloaded blob as an upload
        this.fileList = this.fileList.filter(listFile => listFile.uppyId != fileListInfo.uppyId)

        let blob = new Blob([response.data])
        this.$options.uppy.addFile({
          name: fileListInfo.name,
          type: blob.type,
          data: blob
        })

        this.emitChangeEvent()

      })
      .catch(err => {
        // Failed. Remove download from the list
        this.fileList = this.fileList.filter(listFile => listFile.uppyId != fileListInfo.uppyId)

        this.emitChangeEvent()
      })
    },
    removeFile(fileInfo) {
      this.initUppy()

      if(fileInfo.downloading) {
        if(fileInfo.abortCtrl) {

          fileInfo.abortCtrl.abort()
          this.fileList = this.fileList.filter(listFile => listFile.uppyId != fileInfo.uppyId)
        }
      } else {
        this.$options.uppy.removeFile(fileInfo.uppyId)
      }
    },
    filesDroppedFromTarget(dropFileList) {
      this.initUppy()
      
      if(this.autoUpload) {
        dropFileList.forEach(file => {
          this.$options.uppy.addFile({
            source: file.id,
            name: file.name,
            type: file.type,
            data: file,
            meta: {
              relativePath: file.relativePath || null
            }
          })
        })
      }
    },
    onBrowse() {
      this.initUppy()

      this.$refs.fileInput.click()
    },
    onFileInputChange(event) {
      this.initUppy()

      const files = Array.prototype.slice.call(event.target.files || [], 0)

      if(this.autoUpload) {
        files.forEach(file => {
          this.$options.uppy.addFile({
            source: file.id,
            name: file.name,
            type: file.type,
            data: file,
            meta: {
              relativePath: file.relativePath || null
            }
          })
        })
      }

      this.$emit('fileSelected', files)

      // Clear files value to fix issues on some browsers
      event.target.value = null
    },

    validateUploadsComplete() {
      // Validates that all uploads / downloads (if any) are complete before form submission
      // Displays errors here & returns false if upload not ready
      if(this.fileList.filter(file => file.uploading || file.downloading).length > 0) {
        this.warningMessage('Uploads Active', 'Please wait for all active uploads to be completed')
        return false
      }      

      return true
    }
  }
};
</script>
